import type { NumberValue } from 'd3-scale';

import type { AnyScale } from '../../../../core/types/scales.js';
import { isBandScale } from '../../../../core/utils/detect-scale.js';
import {
  hasHorizontalLabelCollision,
  type ContinuousScale,
  TICK_LABEL_GAP_PX,
} from '../../../../core/utils/has-horizontal-label-collision.js';

/**
 * Applies collision-aware clamping to a tick-label truncation budget.
 *
 * When exactly two continuous-scale ticks collide, this function shrinks
 * `baseMax` so that each label only occupies its fair share of the pixel gap
 * between the two tick positions. The share depends on domain-boundary
 * alignment: a boundary-anchored label uses less space than a centered one.
 *
 * Returns `baseMax` unchanged whenever the conditions for collision detection
 * are not met (categorical, rotated, ≠2 ticks, band scale, or no collision).
 */
export function computeCollisionAwareMaxSpace(
  baseMax: number,
  formattedTicks: string[],
  rawTicks: NumberValue[],
  scale: AnyScale | undefined,
  isCategoricalAxis: boolean,
  isTickRotated: boolean,
): number {
  if (
    isCategoricalAxis ||
    isTickRotated ||
    rawTicks.length !== 2 ||
    !scale ||
    isBandScale(scale)
  ) {
    return baseMax;
  }

  const continuousScale = scale as unknown as ContinuousScale;
  const domain = continuousScale.domain() as [number | Date, number | Date];
  const domainBounds: [number, number] = [+domain[0], +domain[1]];

  if (
    !hasHorizontalLabelCollision(
      formattedTicks,
      rawTicks,
      continuousScale,
      domainBounds,
    )
  ) {
    return baseMax;
  }

  const pos0 = continuousScale(rawTicks[0]);
  const pos1 = continuousScale(rawTicks[1]);
  const pixelGap = pos1 - pos0;

  const t0IsStart = +rawTicks[0] === domainBounds[0];
  const t1IsEnd = +rawTicks[1] === domainBounds[1];

  const bothAtDomainEdges = t0IsStart && t1IsEnd;
  const oneAtDomainEdge = t0IsStart || t1IsEnd;

  const gapAfterPadding = pixelGap - TICK_LABEL_GAP_PX;

  const tighterMax = bothAtDomainEdges
    ? gapAfterPadding / 2
    : oneAtDomainEdge
    ? (2 * gapAfterPadding) / 3
    : gapAfterPadding;

  return Math.max(tighterMax, 0);
}
